// Helpers around the `/api/clarity/me` usage payload (per-user monthly
// recording-hours quota tracked server-side in seconds).

// Minimum remaining budget required to start a new session. Mirrors
// internal/domain/user.go MinSessionStartSeconds — keep in sync.
export const MIN_SESSION_START_SECONDS = 300 // 5 minutes

export function hasHoursQuota(usage) {
  return !!(usage && usage.monthly_seconds_quota)
}

// Remaining seconds in the current period; never negative.
export function remainingSeconds(usage) {
  if (!hasHoursQuota(usage)) return 0
  const r = usage.monthly_seconds_quota - (usage.monthly_seconds_used || 0)
  return r > 0 ? r : 0
}

// True when the user has used 100% of their quota.
export function isAtQuota(usage) {
  return hasHoursQuota(usage) && remainingSeconds(usage) <= 0
}

// True when there's some budget left but it's below the start threshold —
// the user can't start a new session, but they're not strictly maxed out.
export function isBelowStartThreshold(usage) {
  if (!hasHoursQuota(usage)) return false
  const r = remainingSeconds(usage)
  return r > 0 && r < MIN_SESSION_START_SECONDS
}

// Ratio of used to quota, clamped to [0, 1]. Returns null when there's no
// quota set (legacy / dev-mode rows).
export function usageRatio(usage) {
  if (!hasHoursQuota(usage)) return null
  const r = (usage.monthly_seconds_used || 0) / usage.monthly_seconds_quota
  if (r < 0) return 0
  if (r > 1) return 1
  return r
}

// Used hours, one decimal under 10h, integer beyond. Avoids "0.0 h" when
// nothing's been recorded yet.
export function formatHoursUsed(seconds) {
  const s = Number(seconds) || 0
  if (s <= 0) return '0 h'
  const h = s / 3600
  if (h < 10) return `${h.toFixed(1)} h`
  return `${Math.round(h)} h`
}

// Quota hours — always rounded to integer; the quota is a round figure we set.
export function formatHoursQuota(seconds) {
  return `${Math.round((Number(seconds) || 0) / 3600)} h`
}
